import React, { useState, useEffect, useCallback, memo } from 'react';
import { Input, Label } from '../../css/Shared';
import { useAppContext } from '../../context/AppContext';
import { toast } from 'react-toastify';
import Papa from 'papaparse';
import api from '../../components/Api';

/* ---------- Isolated new suppression address form ---------- */
const AddAddressRow = memo(({ onAdd }) => {
  const [newAddr, setNewAddr] = useState({
    prefix:'', firstName:'', lastName:'', middleName:'', company:'', suffix:'',
    address1:'', address2:'', address3:'', city:'', state:'', zip:''
  });

  const handleChange = useCallback((key) => (e) => {
    setNewAddr(p => ({ ...p, [key]: e.target.value }));
  }, []);

  const handleAdd = useCallback(() => {
    if (!newAddr.address1) { toast.error('Address1 is required'); return; }
    onAdd({ ...newAddr, seed: 'N' }); // ensure seed field exists
    setNewAddr({
      prefix:'', firstName:'', lastName:'', middleName:'', company:'', suffix:'',
      address1:'', address2:'', address3:'', city:'', state:'', zip:''
    });
  }, [newAddr, onAdd]);

  return (
    <div className="grid grid-cols-2 md:grid-cols-6 lg:grid-cols-12 gap-2 items-center">
      {['prefix','firstName','lastName','middleName','company','suffix','address1','address2','address3','city','state','zip'].map(key => (
        <Input
          key={key}
          placeholder={key.charAt(0).toUpperCase() + key.slice(1)}
          value={newAddr[key]}
          onChange={handleChange(key)}
        />
      ))}
      <button type="button" onClick={handleAdd} className="btn-primary">Add</button>
    </div>
  );
});

/* ---------- Main Component ---------- */
const SuppressionListForm = ({ suppressionList, onClose, setSuppressionLists, reset, setReset }) => {
  const { authState } = useAppContext();

  const [formData, setFormData] = useState({
    suppressionListName:'',
    description:'',
    mailOwner:'',
    bmeuFacility:null,
  company: authState?.user.company?._id || '' // ensure always a string
  });

  const [addresses, setAddresses] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [csvMapping, setCsvMapping] = useState({});

  /* ---------- Initialize edit mode ---------- */
  useEffect(() => {
    if (suppressionList?._id) {
      setFormData({
        suppressionListName: suppressionList.suppressionListName || '',
        description: suppressionList.description || '',
        mailOwner: suppressionList.mailOwner || '',
        bmeuFacility: suppressionList.bmeuFacility || null,
         company: suppressionList.company || authState?.user.company?._id || '' // <- always set
      });
      setAddresses(
        (suppressionList.seeds || []).map(addr => ({ ...addr, seed: addr.seed || 'N' }))
      );
    }
  }, [suppressionList, authState?.user.company?._id]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleAddAddress = useCallback((addr) => {
    setAddresses(prev => [...prev, addr]);
  }, []);

  const removeAddress = useCallback((index) => {
    setAddresses(prev => prev.filter((_, i) => i !== index));
  }, []);

  /* ---------- CSV Upload ---------- */
  const handleCsvUpload = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        let allRows = results.data || [];
        const MAX_RECORDS = 2500;

        if (allRows.length > MAX_RECORDS) {
          allRows = allRows.slice(0, MAX_RECORDS);
          toast.info(`CSV has ${results.data.length} rows — only first ${MAX_RECORDS} loaded.`);
        } else if (allRows.length > 0) {
          toast.success(`CSV loaded (${allRows.length} rows). Map headers.`);
        } else {
          toast.warn('CSV empty or invalid.');
        }

        setCsvHeaders(allRows.length > 0 ? Object.keys(allRows[0] || {}) : []);
        setCsvRows(allRows);
      },
      error: (error) => {
        console.error(error);
        toast.error('Failed to parse CSV.');
      }
    });
  }, []);

  const handleMappingChange = useCallback((field, value) => {
    setCsvMapping(prev => ({ ...prev, [field]: value }));
  }, []);

  const applyCsvMapping = useCallback(() => {
    const requiredFields = ['prefix','firstName','lastName','middleName','company','suffix','address1','address2','address3','city','state','zip'];
    const mapped = csvRows.map(row => {
      const newRow = {};
      requiredFields.forEach(f => {
        const col = csvMapping[f];
        newRow[f] = col ? row[col] : '';
      });
      newRow.seed = 'N'; // ensure seed field
      return newRow;
    });
    setAddresses(prev => [...prev, ...mapped]);
    setCsvHeaders([]);
    setCsvRows([]);
    setCsvMapping({});
    toast.success('CSV addresses added');
  }, [csvRows, csvMapping]);

  /* ---------- Submit ---------- */
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const isUpdate = Boolean(suppressionList?._id);
    try {
   const payload = { ...formData, company: formData.company || authState?.user.company?._id, seeds: addresses };
      const saved = isUpdate
        ? await api.put(`/permits/suppress/update/${suppressionList._id}`, payload)
        : await api.post(`/permits/suppress/new`, payload);
if (typeof setSuppressionLists === 'function') {
  setSuppressionLists(prev =>
    isUpdate
      ? prev.map(sl => sl._id === saved.data._id ? saved.data : sl)
      : [saved.data, ...prev] // <- newly added list at top
  );

  // Also, if this is a new list, call a callback to select it in parent
  if (!isUpdate && typeof reset === 'function') {
    // reset here can be replaced with a callback like setSelectedSuppressionListId in parent
    setReset(saved.data._id); // <-- send the new list ID to parent
  }
}

      toast.success(isUpdate ? 'Suppression list updated' : 'Suppression list created');
      if (typeof setReset === 'function') setReset(r => !r);
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || err.message || 'Error saving list');
    }
  }, [formData, addresses, suppressionList, setSuppressionLists, setReset, onClose]);

  return (
    <div className="max-h-[80vh] overflow-y-auto max-w-6xl mx-auto px-6 py-4 text-sm [&_input]:h-9 [&_input]:text-sm [&_input]:px-3 [&_label]:text-xs">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-base font-semibold text-gray-800">
            {suppressionList ? 'Edit Suppression List' : 'New Suppression List'}
          </h2>
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">Save</button>
          </div>
        </div>
<input type="hidden" name="company" value={formData.company} />
        {/* Suppression List Info */}
        <section className="border rounded-lg p-4">
          <h3 className="text-xs font-medium text-gray-500 mb-3 uppercase tracking-wide">Suppression List Info</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputGroup label="Suppression list name" name="suppressionListName" defaultValue={formData.suppressionListName} onChange={handleChange} />
            <InputGroup label="Description" name="description" defaultValue={formData.description} onChange={handleChange} />
          </div>
        </section>

        {/* Addresses */}
        <section className="border rounded-lg p-4">
          <h3 className="text-xs font-medium text-gray-500 mb-3 uppercase tracking-wide">Suppression Addresses</h3>
          <AddAddressRow onAdd={handleAddAddress} />

          {/* CSV Upload */}
          <div className="mt-4 space-y-2">
            <Label muted>Upload CSV</Label>
            <input type="file" accept=".csv" onChange={handleCsvUpload} className="border rounded p-1 w-full text-sm" />
            {csvHeaders.length > 0 && (
              <div className="mt-2 border p-2 rounded bg-gray-50 space-y-2">
                <h4 className="text-xs font-medium text-gray-500 mb-2">Map CSV Columns</h4>
                {['prefix','firstName','lastName','middleName','company','suffix','address1','address2','address3','city','state','zip'].map(field => (
                  <div key={field} className="flex flex-col md:flex-row items-start md:items-center gap-2">
                    <Label muted className="w-24 capitalize">{field.replace(/([A-Z])/g,' $1')}</Label>
                    <select
                      value={csvMapping[field] || ''}
                      onChange={e => handleMappingChange(field, e.target.value)}
                      className="border rounded p-1 text-sm flex-1"
                    >
                      <option value="">Select CSV Column</option>
                      {csvHeaders.map(header => (
                        <option key={header} value={header}>{header}</option>
                      ))}
                    </select>
                  </div>
                ))}
                <button type="button" onClick={applyCsvMapping} className="btn-primary mt-2">Add CSV Addresses</button>
              </div>
            )}
          </div>

          {/* Addresses Table */}
          {addresses.length > 0 && (
            <div className="mt-4 border rounded bg-gray-50 overflow-x-auto">
              <table className="w-full text-sm table-fixed border-collapse bg-white">
                <thead className="text-xs text-gray-500 uppercase border-b bg-gray-100 sticky top-0 z-20 shadow-sm">
                  <tr>
                    {['prefix','firstName','lastName','middleName','company','suffix','address1','address2','address3','city','state','zip','seed'].map(h => (
                      <th key={h} className="px-3 py-2 text-left font-medium truncate bg-gray-100 z-20">{h}</th>
                    ))}
                    <th className="px-3 py-2">Remove</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {addresses.map((addr,i) => (
                    <tr key={i} className="border-b hover:bg-gray-50">
                      {['prefix','firstName','lastName','middleName','company','suffix','address1','address2','address3','city','state','zip','seed'].map(key => (
                        <td key={key} className="px-3 py-2 truncate">{addr[key] || '—'}</td>
                      ))}
                      <td className="px-3 py-2">
                        <button onClick={() => removeAddress(i)} className="text-red-500 text-xs hover:text-red-700">Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        </section>

      </form>
    </div>
  );
};

/* ---------- InputGroup helper ---------- */
const InputGroup = memo(({ label, name, defaultValue, onChange }) => (
  <div>
    <Label muted>{label}</Label>
    <Input name={name} defaultValue={defaultValue} onChange={onChange} />
  </div>
));

export default SuppressionListForm;